import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Loader2, Sliders, RefreshCw } from "lucide-react";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

/**
 * DietConditionDefaults — clinic-wide macro tuner.
 *
 * The Advanced Diet Chart ships with sensible defaults per condition (0.8
 * g/kg protein for CKD, 2000 mg Na for HF, etc.) but every diabetologist has
 * their own preferred numbers. This page lets the doctor override those
 * defaults once, and every future plan uses them.
 *
 * Editable fields per condition code:
 *   protein_gkg, sodium_mg, fluid_ml, cooking_oil_tsp
 * Empty fields fall through to the built-in defaults.
 */
export default function DietConditionDefaults() {
  const nav = useNavigate();
  const [codes, setCodes] = useState([]);
  const [defaults, setDefaults] = useState({}); // { CODE: {protein_gkg,...} }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, d] = await Promise.all([
          api.get("/diet/condition-codes"),
          api.get("/diet/condition-defaults"),
        ]);
        setCodes(c.data?.conditions || []);
        setDefaults(d.data?.defaults || {});
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load defaults");
      } finally { setLoading(false); }
    })();
  }, []);

  const setField = (code, key, value) => {
    setDefaults((prev) => ({
      ...prev,
      [code]: { ...(prev[code] || {}), [key]: value },
    }));
  };
  const clearRow = (code) => {
    setDefaults((prev) => {
      const next = { ...prev };
      delete next[code];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/diet/condition-defaults", { defaults });
      setDefaults(r.data?.defaults || {});
      toast.success("Defaults saved — future Advanced Diet plans will use them");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Layout>
      <Toaster position="top-right" richColors />
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <Button variant="ghost" size="sm" onClick={() => nav(-1)} className="gap-1"><ArrowLeft size={14} /> Back</Button>
          <div className="flex items-center gap-2 ml-2">
            <Sliders size={16} className="text-pink-600" />
            <h1 className="text-lg font-heading font-semibold">Diet — Condition Defaults</h1>
          </div>
        </div>
        <div className="text-xs text-slate-600 leading-relaxed">
          Override the built-in macro defaults for any condition. Leave a field <em>blank</em> to keep the built-in default. When a patient has multiple conditions, later conditions in the profile win ties.
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : (
          <>
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
              <div className="grid grid-cols-6 gap-2 px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                <div className="col-span-2">Condition</div>
                <div>Protein g/kg</div>
                <div>Sodium mg</div>
                <div>Fluid ml</div>
                <div>Oil tsp</div>
              </div>
              <div className="divide-y divide-slate-100">
                {codes.map((c) => {
                  const row = defaults[c.code] || {};
                  const hasAny = ["protein_gkg", "sodium_mg", "fluid_ml", "cooking_oil_tsp"].some((k) => row[k] !== undefined && row[k] !== "");
                  return (
                    <div key={c.code} className="grid grid-cols-6 gap-2 px-4 py-2 items-center" data-testid={`tuner-row-${c.code}`}>
                      <div className="col-span-2">
                        <div className="text-sm font-medium text-slate-800">{c.label}</div>
                        <div className="text-[10px] text-slate-500">{c.code}</div>
                      </div>
                      {[
                        ["protein_gkg", "0.8", "number", "0.05"],
                        ["sodium_mg", "2000", "number", "50"],
                        ["fluid_ml", "1500", "number", "50"],
                        ["cooking_oil_tsp", "3", "number", "1"],
                      ].map(([k, ph, t, step]) => (
                        <Input
                          key={k}
                          type={t}
                          step={step}
                          value={row[k] ?? ""}
                          onChange={(e) => setField(c.code, k, e.target.value)}
                          placeholder={ph}
                          className="h-8 text-sm"
                          data-testid={`tuner-${c.code}-${k}`}
                        />
                      ))}
                      <div className="col-span-6 flex justify-end -mt-1">
                        {hasAny && (
                          <button
                            type="button"
                            onClick={() => clearRow(c.code)}
                            className="text-[10px] text-rose-600 hover:underline inline-flex items-center gap-1"
                            data-testid={`tuner-clear-${c.code}`}
                          >
                            <RefreshCw size={10} /> Reset to built-in
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 sticky bottom-0 bg-transparent pt-2">
              <Button onClick={save} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white gap-2" data-testid="tuner-save-btn">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {saving ? "Saving…" : "Save defaults"}
              </Button>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
